import { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import { citationRoute } from '../../components/inspectors/AiCitationChip.jsx';
import {
  PageHeader,
  ErrorState,
  PageViewerControls,
  KpiHeroBand,
  StatusDonutMini,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import './search.redesign.css';

// Entity categories the global search can return (AC-SHELL-2). `tone` is a
// *category* color (which kind of record), drawn from the executive-traffic-light
// tokens. It is never a per-result health/status — the /search API returns no
// such field.
const GROUPS = [
  { type: 'rapid', label: 'RAPIDs', tone: 'amber', icon: '⚡' },
  { type: 'portfolio', label: 'Portfolios', tone: 'blue', icon: '🗂️' },
  { type: 'program', label: 'Programs', tone: 'sage', icon: '🛰️' },
  { type: 'project', label: 'Projects', tone: 'blue', icon: '📁' },
  { type: 'briefing', label: 'Briefings', tone: 'slate', icon: '📑' },
  { type: 'artifact', label: 'Knowledge artifacts', tone: 'sage', icon: '📚' },
  { type: 'lesson', label: 'Lessons learned', tone: 'amber', icon: '🧭' },
  { type: 'action', label: 'Actions', tone: 'brick', icon: '✅' },
  { type: 'meeting', label: 'Meetings', tone: 'blue', icon: '🗓️' },
  { type: 'person', label: 'People', tone: 'slate', icon: '👤' },
];

const GROUP_BY_TYPE = new Map(GROUPS.map((g) => [g.type, g]));

function groupMeta(type) {
  return GROUP_BY_TYPE.get(type) || { type, label: type, tone: 'slate', icon: '🔎' };
}

// Route resolution per row. The API route is authoritative; when absent we fall
// back to the shared id-prefix table (citationRoute) instead of duplicating a
// local routing map. People never link out: /me is self-only and no shared
// user-profile route exists, so person rows render honestly inert.
function resultRoute(row) {
  if (row.type === 'person') return null;
  return row.route || citationRoute(row.id);
}

function SearchQueryForm({ q, onSubmit }) {
  return (
    <form
      className="search-query glass-panel"
      role="search"
      aria-label="Search DHA PRIME"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(String(new FormData(event.currentTarget).get('q') || ''));
      }}
    >
      <span className="search-query__icon" aria-hidden="true">
        🔎
      </span>
      <input
        key={q}
        className="search-query__input"
        type="search"
        name="q"
        defaultValue={q}
        placeholder="Search portfolios, projects, knowledge, actions, meetings, people…"
        aria-label="Search query"
      />
      <button type="submit" className="search-query__submit">
        Search
      </button>
    </form>
  );
}

// Clickable type facets. Counts are the server-side per-type totals from the
// /search `facets` block — never recomputed from the rows on this page.
function SearchFacetBar({ facets, activeType, onSelect }) {
  if (!facets.types.length) return null;
  return (
    <div
      className="search-facets"
      role="group"
      aria-label="Filter results by record type"
      data-testid="search-facets"
    >
      <button
        type="button"
        className={`search-facet-chip tone-slate${activeType ? '' : ' is-active'}`}
        aria-pressed={!activeType}
        onClick={() => onSelect('')}
      >
        All types
        <span className="search-facet-chip__count">{facets.grandTotal}</span>
      </button>
      {facets.types.map((facet) => {
        const meta = groupMeta(facet.type);
        const active = activeType === facet.type;
        return (
          <button
            key={facet.type}
            type="button"
            className={`search-facet-chip tone-${meta.tone}${active ? ' is-active' : ''}`}
            aria-pressed={active}
            onClick={() => onSelect(facet.type)}
          >
            <span aria-hidden="true">{meta.icon}</span>
            {meta.label}
            <span className="search-facet-chip__count">{facet.total}</span>
          </button>
        );
      })}
    </div>
  );
}

function SearchResultRow({ row, tone }) {
  const to = resultRoute(row);
  if (to) {
    return (
      <Link to={to} className={`search-link tone-${tone}`}>
        <span className="search-link__rail" aria-hidden="true" />
        <span className="search-link__body">
          <span className="search-link__name">{row.name}</span>
          {row.summary ? <span className="search-link__summary">{row.summary}</span> : null}
          <span className="search-link__id">{row.id}</span>
        </span>
        <span className="search-link__open" aria-hidden="true">
          Open →
        </span>
      </Link>
    );
  }
  const isPerson = row.type === 'person';
  return (
    <span
      className={`search-link search-link--inert tone-${tone}`}
      title={
        isPerson ? `${row.name} (${row.id}) — no linked profile view` : 'No linked record view'
      }
    >
      <span className="search-link__rail" aria-hidden="true" />
      <span className="search-link__body">
        <span
          className={`search-link__name${isPerson ? ' person-label' : ''}`}
          data-user-id={isPerson ? row.id : undefined}
        >
          {row.name}
        </span>
        {row.summary ? <span className="search-link__summary">{row.summary}</span> : null}
        <span className="search-link__id">{row.id}</span>
      </span>
      <span className="search-link__open search-link__open--inert" aria-hidden="true">
        {isPerson ? 'Directory listing' : 'No record view'}
      </span>
    </span>
  );
}

export function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const q = (params.get('q') || '').trim();
  const typeFilter = (params.get('type') || '').trim();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });

  const search = useApi('/search', {
    query: {
      q,
      limit: pager.pageSize,
      offset: pager.offset,
      ...(typeFilter ? { type: typeFilter } : {}),
    },
    skip: !q,
  });

  // Same-query re-run from the top bar (PRIME-SEARCH-DEF-001, ticket §7 lead 2):
  // when the query string is unchanged, `useApi`'s key-driven effect does not
  // refire, so a top-bar resubmit would look like "search did nothing". TopBar
  // attaches a monotonic `searchSubmit` nonce to the navigation's location
  // state; a nonce change with an unchanged query means an explicit resubmit —
  // force a refetch. A nonce that arrives together with a changed query is a
  // genuine new search that the key-driven effect already handles, so skip it
  // here to avoid a double fetch.
  const submitNonce = location.state?.searchSubmit;
  const lastSubmitRef = useRef({ nonce: submitNonce, q });
  const refresh = search.refresh;
  useEffect(() => {
    const prev = lastSubmitRef.current;
    if (submitNonce !== prev.nonce && q && q === prev.q) refresh();
    lastSubmitRef.current = { nonce: submitNonce, q };
  }, [submitNonce, q, refresh]);

  const applyQuery = (term) => {
    const value = String(term || '').trim();
    // Resubmitting the identical term would be a no-op URL write and would not
    // refetch; re-run the search explicitly instead (ticket §7 lead 2).
    if (value && value === q) {
      search.refresh();
      return;
    }
    const next = new URLSearchParams(params);
    if (value) next.set('q', value);
    else next.delete('q');
    next.delete('offset');
    setParams(next);
  };

  const applyType = (type) => {
    const next = new URLSearchParams(params);
    if (type) next.set('type', type);
    else next.delete('type');
    next.delete('offset');
    setParams(next);
  };

  const grouped = useMemo(() => {
    const rows = search.data?.data || [];
    const out = new Map(GROUPS.map((g) => [g.type, []]));
    for (const row of rows) {
      if (!out.has(row.type)) out.set(row.type, []);
      out.get(row.type).push(row);
    }
    return out;
  }, [search.data]);

  if (!q) {
    return (
      <div className="search-redesign">
        <PageHeader
          eyebrow="Global search"
          title="Search DHA PRIME"
          subtitle="Find portfolios, programs, projects, RAPIDs, briefings, knowledge, lessons, actions, meetings, and people across the engine."
        />
        <SearchQueryForm q={q} onSubmit={applyQuery} />
        <BrandedEmptyState
          icon="🔎"
          eyebrow="Ready when you are"
          title="Search DHA PRIME"
          description="Type a search term above (or in the topbar) and press Enter — results cover every record type, with type chips to narrow down."
          tone="blue"
        />
      </div>
    );
  }

  // `search.stale` covers the first render after a query/facet change, before
  // the load effect flips `loading`: the retained rows belong to the PREVIOUS
  // query, so render the loading state rather than old rows under the new
  // query/heading/counts (PRIME-SEARCH-DEF-001, ticket §7 lead 1).
  if (search.loading || search.stale)
    return (
      <div className="search-redesign">
        <PageHeader eyebrow="Global search" title={`Results for “${q}”`} />
        <SearchQueryForm q={q} onSubmit={applyQuery} />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <TableSkeleton rows={6} />
        </div>
      </div>
    );
  if (search.error)
    return (
      <div className="search-redesign">
        <PageHeader eyebrow="Global search" title={`Results for “${q}”`} />
        <SearchQueryForm q={q} onSubmit={applyQuery} />
        <ErrorState error={search.error} />
      </div>
    );

  const facets = search.data?.facets || { grandTotal: 0, types: [] };
  const preview = search.data?.preview || null;
  const total = search.data?.page?.total ?? 0;
  const showing = search.data?.data?.length ?? 0;
  const activeMeta = typeFilter ? groupMeta(typeFilter) : null;
  // In the unfiltered view the server caps each type's contribution; the facet
  // grand total is the real cross-type match count.
  const cappedView = !typeFilter && preview?.mode === 'per_type' && preview.truncated;

  if (facets.grandTotal === 0) {
    return (
      <div className="search-redesign">
        <PageHeader eyebrow="Global search" title={`Results for “${q}”`} />
        <SearchQueryForm q={q} onSubmit={applyQuery} />
        <BrandedEmptyState
          icon="🛰️"
          eyebrow="No matches"
          title={`No matches for “${q}”`}
          description="DHA PRIME search matches names, summaries, and record IDs across every record type — but nothing matched this term."
          tone="slate"
          action={
            <div className="search-tips" data-testid="search-no-results-tips">
              <span className="search-tips__label">Search covers</span>
              <ul className="search-tips__chips">
                {GROUPS.map((g) => (
                  <li key={g.type} className={`search-type-chip tone-${g.tone}`}>
                    <span className="search-tip__icon" aria-hidden="true">
                      {g.icon}
                    </span>
                    {g.label}
                  </li>
                ))}
              </ul>
              <p className="search-tips__hint">
                Try a portfolio short name, program code, project name, briefing keyword, action
                title, meeting name, or a person&rsquo;s name.
              </p>
            </div>
          }
        />
      </div>
    );
  }

  if (total === 0) {
    // A type facet is active (or stale in the URL) but has no matches; other
    // types do — keep the facet bar so the user can pivot without retyping.
    return (
      <div className="search-redesign">
        <PageHeader eyebrow="Global search" title={`Results for “${q}”`} />
        <SearchQueryForm q={q} onSubmit={applyQuery} />
        <SearchFacetBar facets={facets} activeType={typeFilter} onSelect={applyType} />
        <BrandedEmptyState
          icon="🛰️"
          eyebrow="No matches in this type"
          title={`No ${activeMeta ? activeMeta.label.toLowerCase() : typeFilter} matches for “${q}”`}
          description={`Other record types hold ${facets.grandTotal} ${
            facets.grandTotal === 1 ? 'match' : 'matches'
          } for this term — pick another type chip above.`}
          tone="slate"
        />
      </div>
    );
  }

  // KPI hero band: server-side totals only — the grand total plus the top type
  // facets, exactly as reported by the /search facets block.
  const heroItems = facets.types.slice(0, 4).map((facet) => {
    const meta = groupMeta(facet.type);
    return {
      id: facet.type,
      label: meta.label,
      value: facet.total,
      icon: meta.icon,
      status: meta.tone,
      statusLabel: 'Server total',
      footer: facet.total === 1 ? '1 match' : `${facet.total} matches`,
    };
  });
  heroItems.unshift({
    id: 'all',
    label: 'Total matches',
    value: facets.grandTotal,
    unit: facets.grandTotal === 1 ? 'record' : 'records',
    icon: '🔎',
    status: 'blue',
    statusLabel: typeFilter ? 'Filtered' : 'All types',
    highlight: true,
    footer: typeFilter
      ? `Filtered to ${activeMeta.label} — ${total} ${total === 1 ? 'match' : 'matches'}`
      : `${showing} of ${total} shown on this page`,
  });

  // Type-mix ring — category distribution across the loaded page (on-page
  // counts, honestly labeled as such).
  const onPageByType = GROUPS.map((g) => ({
    ...g,
    count: (grouped.get(g.type) || []).length,
  })).filter((g) => g.count > 0);
  const mixSegments = onPageByType.map((g) => ({
    label: g.label,
    count: g.count,
    tone: g.tone,
  }));

  return (
    <section className="search-redesign">
      <PageHeader eyebrow="Global search" title={`Results for “${q}”`} />

      <SearchQueryForm q={q} onSubmit={applyQuery} />

      <SearchFacetBar facets={facets} activeType={typeFilter} onSelect={applyType} />

      <div className="search-summary" data-testid="search-results-summary">
        Showing {showing} of {total} {activeMeta ? `${activeMeta.label} ` : ''}matches for{' '}
        <strong>&ldquo;{q}&rdquo;</strong>
        {cappedView ? (
          <span className="muted">
            · top results per type — pick a type chip to browse all {facets.grandTotal}
          </span>
        ) : showing < total ? (
          <span className="muted">· paged results below</span>
        ) : null}
      </div>

      <KpiHeroBand
        eyebrow="Results by type"
        context={typeFilter ? `Filtered · ${activeMeta.label}` : 'Server totals'}
        items={heroItems}
        columns={Math.min(5, heroItems.length)}
      />

      {mixSegments.length > 1 ? (
        <div className="search-mix glass-panel">
          <StatusDonutMini
            segments={mixSegments}
            size={104}
            totalLabel="On page"
            centerValue={showing}
            ariaLabel={`Result type mix for ${q}`}
          />
          <div className="search-mix__copy">
            <span className="search-mix__title">Result mix on this page</span>
            <p className="search-mix__note">
              Color encodes record type only. DHA PRIME search matches names, summaries, and IDs —
              it does not return a per-result status, so none is shown here.
            </p>
          </div>
        </div>
      ) : null}

      <StaggerReveal className="stack" style={{ gap: 16 }}>
        {GROUPS.map((g) => {
          const rows = grouped.get(g.type) || [];
          if (!rows.length) return null;
          const serverTotal = facets.types.find((f) => f.type === g.type)?.total ?? rows.length;
          return (
            <div key={g.type} className={`search-group card tone-${g.tone}`}>
              <h2 className="search-group__head card-title">
                <span className="search-group__icon" aria-hidden="true">
                  {g.icon}
                </span>
                <span className="search-group__label">{g.label}</span>
                <span className={`search-type-chip tone-${g.tone}`}>
                  <span className="search-type-chip__dot" aria-hidden="true" />
                  {rows.length === serverTotal
                    ? `${rows.length} ${rows.length === 1 ? 'match' : 'matches'}`
                    : `${rows.length} of ${serverTotal} on this page`}
                </span>
              </h2>
              <ul className="search-list">
                {rows.map((row) => (
                  <li key={`${row.type}-${row.id}`} className="search-item">
                    <SearchResultRow row={row} tone={g.tone} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </StaggerReveal>

      <PageViewerControls
        page={search.data?.page}
        label="matches"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </section>
  );
}
